import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { formatDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CaretLeft, CaretRight, CalendarBlank, ListBullets } from "@phosphor-icons/react";

const MONTHS = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];
const DAYS = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
const pad = (n) => String(n).padStart(2, "0");
const isoOf = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;
const KIND = { in: ["Übergabe", "bg-emerald-50 border-emerald-300 text-emerald-900"], out: ["Rücknahme", "bg-amber-50 border-amber-300 text-amber-900"] };
const PROTO = { draft: "Protokoll in Arbeit", signed: "Protokoll unterschrieben" };

function Chip({ it }) {
  const [label, cls] = KIND[it.kind];
  return (
    <Link to={`/mietverhaeltnisse/${it.tenancy_id}?tab=protocols`} title={`${label} · ${it.tenants.join(", ")} · ${it.location}`}
      className={`block truncate rounded border px-1.5 py-0.5 text-[11px] leading-tight hover:brightness-95 ${cls}`} data-testid={`cal-chip-${it.tenancy_id}-${it.kind}`}>
      {it.time && <b className="tabular-nums">{it.time} </b>}{label} · {it.tenants[0] || it.contract_number}
    </Link>
  );
}

function MonthGrid({ y, m, items }) {
  const first = (new Date(y, m, 1).getDay() + 6) % 7;
  const days = new Date(y, m + 1, 0).getDate();
  const today = new Date().toISOString().slice(0, 10);
  const by = useMemo(() => items.reduce((a, it) => ({ ...a, [it.date]: [...(a[it.date] || []), it] }), {}), [items]);
  const cells = [...Array(first).fill(null), ...Array.from({ length: days }, (_, i) => i + 1)];
  return (
    <div className="grid grid-cols-7 border-l border-t border-slate-200 bg-white" data-testid="cal-month-grid">
      {DAYS.map((d) => <div key={d} className="border-r border-b border-slate-200 bg-slate-50 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">{d}</div>)}
      {cells.map((d, i) => {
        const iso = d && isoOf(y, m, d);
        return (
          <div key={i} className={`min-h-[96px] border-r border-b border-slate-200 p-1 space-y-1 ${!d ? "bg-slate-50/50" : ""}`} data-testid={d ? `cal-day-${iso}` : undefined}>
            {d && <div className={`text-xs tabular-nums ${iso === today ? "inline-flex h-5 w-5 items-center justify-center rounded-full bg-slate-900 text-white" : "text-slate-500"}`}>{d}</div>}
            {(by[iso] || []).map((it) => <Chip key={`${it.tenancy_id}-${it.kind}`} it={it} />)}
          </div>
        );
      })}
    </div>
  );
}

function ListView({ items }) {
  if (!items.length) return <div className="rounded-md border border-dashed border-slate-300 bg-white p-8 text-center text-sm text-slate-500" data-testid="cal-empty">Keine Übergaben oder Rücknahmen in diesem Monat.</div>;
  return (
    <div className="bg-white border border-slate-200 rounded-md divide-y divide-slate-100" data-testid="cal-list">
      {items.map((it) => (
        <Link key={`${it.tenancy_id}-${it.kind}`} to={`/mietverhaeltnisse/${it.tenancy_id}?tab=protocols`} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 text-sm hover:bg-slate-50" data-testid={`cal-row-${it.tenancy_id}-${it.kind}`}>
          <span className="w-36 tabular-nums font-medium">{formatDate(it.date)}{it.time ? `, ${it.time} Uhr` : ""}</span>
          <span className={`rounded border px-2 py-0.5 text-[11px] font-semibold ${KIND[it.kind][1]}`}>{KIND[it.kind][0]}</span>
          <span className="w-48 truncate">{it.tenants.join(", ") || "—"}</span>
          <span className="font-mono text-xs text-slate-500">{it.contract_number}</span>
          <span className="flex-1 min-w-[200px] truncate text-slate-600">{it.location}</span>
          <span className={`text-xs ${it.protocol_status === "signed" ? "text-emerald-700" : "text-slate-500"}`}>{PROTO[it.protocol_status] || "Noch kein Protokoll"}</span>
        </Link>
      ))}
    </div>
  );
}

export default function Uebergabetermine() {
  const now = new Date();
  const [ym, setYm] = useState([now.getFullYear(), now.getMonth()]);
  const [view, setView] = useState("month");
  const [propertyId, setPropertyId] = useState("all");
  const [properties, setProperties] = useState([]);
  const [items, setItems] = useState([]);
  const [y, m] = ym;
  useEffect(() => { api.get("/properties").then(({ data }) => setProperties(data || [])).catch(() => {}); }, []);
  useEffect(() => {
    const params = { date_from: isoOf(y, m, 1), date_to: isoOf(y, m, new Date(y, m + 1, 0).getDate()) };
    if (propertyId !== "all") params.property_id = propertyId;
    api.get("/handover-appointments", { params }).then(({ data }) => setItems(data || [])).catch(() => setItems([]));
  }, [y, m, propertyId]);
  const shift = (d) => setYm(([yy, mm]) => { const t = new Date(yy, mm + d, 1); return [t.getFullYear(), t.getMonth()]; });
  return (
    <div className="space-y-6" data-testid="uebergabetermine-page">
      <div>
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight">Übergabetermine</h1>
        <p className="mt-2 text-sm text-slate-600">Alle geplanten Wohnungsübergaben und -rücknahmen. Termine werden im Mietverhältnis eingetragen.</p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" size="icon" onClick={() => shift(-1)} data-testid="cal-prev"><CaretLeft size={16} /></Button>
        <div className="min-w-[160px] text-center font-semibold" data-testid="cal-month-label">{MONTHS[m]} {y}</div>
        <Button variant="outline" size="icon" onClick={() => shift(1)} data-testid="cal-next"><CaretRight size={16} /></Button>
        <Button variant="ghost" size="sm" onClick={() => setYm([now.getFullYear(), now.getMonth()])} data-testid="cal-today">Heute</Button>
        <span className="text-xs text-slate-500 ml-2" data-testid="cal-count">{items.length} Termin{items.length === 1 ? "" : "e"}</span>
        <div className="ml-auto flex items-center gap-2">
          <Select value={propertyId} onValueChange={setPropertyId}>
            <SelectTrigger className="w-56 h-9" data-testid="cal-property-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Objekte</SelectItem>
              {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <div className="flex rounded-md border border-slate-200 overflow-hidden">
            <button type="button" onClick={() => setView("month")} className={`px-3 h-9 text-sm inline-flex items-center gap-1 ${view === "month" ? "bg-slate-900 text-white" : "bg-white hover:bg-slate-50"}`} data-testid="cal-view-month"><CalendarBlank size={14} /> Monat</button>
            <button type="button" onClick={() => setView("list")} className={`px-3 h-9 text-sm inline-flex items-center gap-1 ${view === "list" ? "bg-slate-900 text-white" : "bg-white hover:bg-slate-50"}`} data-testid="cal-view-list"><ListBullets size={14} /> Liste</button>
          </div>
        </div>
      </div>
      {view === "month" ? <div className="overflow-x-auto"><div className="min-w-[720px]"><MonthGrid y={y} m={m} items={items} /></div></div> : <ListView items={items} />}
    </div>
  );
}
